import { useState } from 'react'
import { CheckMark } from '@/components/CheckMark'
import type { Question } from '@/lib/parser'
import type { AttemptCounts } from '@/lib/questionAttempts'
import { questionPreview } from '@/lib/questionPreview'
// The chip's label is the Concepts filter's option label, so the two agree.
import { conceptLabel } from '@/lib/questionFilters'
import { MarkdownText, MARKDOWN_LIST_CLASS } from '@/components/MarkdownText'
import { QuestionAnswerReveal } from '@/components/QuestionAnswerReveal'
import { QuestionAttemptBadge } from '@/components/QuestionAttemptBadge'
import { QuestionInfoButton } from '@/components/QuestionInfoButton'

// Renders question markdown (GFM tables + LaTeX) with the same table styling the
// quiz uses, so data-heavy stems (development triangles, etc.) render as tables
// rather than raw pipe text.
const STEM_MD_CLASS =
  `leading-relaxed [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_table]:text-xs [&_th]:text-left [&_td]:pr-4 [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_th]:border [&_th]:border-border [&_th]:px-2 [&_td]:px-2 [&_th]:py-1 [&_td]:py-1 ${MARKDOWN_LIST_CLASS}`

interface QuestionSearchRowProps {
  question: Question
  query: string
  selected?: boolean
  /** Omit on read-only lists (e.g. the exam-history attempt list): the row then
   *  drops its checkbox and click-to-select, leaving Expand as the only action. */
  onToggleSelect?: (id: string) => void
  attemptSummary?: AttemptCounts
  /** True when the viewer's attempt history is actually known (signed in), so an
   *  absent summary means "not attempted" rather than "unknown". */
  attemptsTracked?: boolean
}

/**
 * The collapsed preview — a few lines of the question's prose (see
 * `lib/questionPreview.ts`), with the search match highlighted.
 *
 * The preview is deliberately plain prose so it can be cut at the match, and
 * each piece still goes through the markdown renderer: a ratemaking stem
 * escapes its currency (`\$400`) and writes its variables as math (`$X$`).
 */
function StemPreview({ question, query }: { question: Question; query: string }) {
  const text = questionPreview(question, query)
  const q = query.trim()
  const idx = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1
  if (idx < 0) return <MarkdownText inline>{text}</MarkdownText>
  return (
    <>
      <MarkdownText inline>{text.slice(0, idx)}</MarkdownText>
      <mark className="bg-primary/20 text-foreground rounded px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      <MarkdownText inline>{text.slice(idx + q.length)}</MarkdownText>
    </>
  )
}

export function DifficultyDots({ difficulty }: { difficulty: string }) {
  const count = difficulty === 'easy' ? 1 : difficulty === 'medium' ? 2 : 3
  return (
    <span
      className="flex items-center gap-0.5 shrink-0"
      title={difficulty}
      aria-label={`${difficulty} difficulty`}
    >
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className={`h-2 w-2 rounded-full ${i < count ? 'bg-red-500' : 'bg-muted-foreground/20'}`}
        />
      ))}
    </span>
  )
}

export function QuestionSearchRow({ question, query, selected = false, onToggleSelect, attemptSummary, attemptsTracked = false }: QuestionSearchRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const selectable = !!onToggleSelect

  return (
    <div
      // The whole row toggles selection, so the whole row ticks — not just the
      // checkbox inside it.
      data-sound={selectable ? 'tick' : undefined}
      className={`rounded-lg p-3 space-y-2 transition-colors ${
        selectable ? 'cursor-pointer' : ''
      } ${selected ? 'bg-primary/5' : selectable ? 'hover:bg-accent/30' : ''}`}
      onClick={selectable ? () => onToggleSelect(question.id) : undefined}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <DifficultyDots difficulty={question.difficulty} />
          {selectable && (
            <div
              role="checkbox"
              aria-checked={selected}
              aria-label={`Select question ${question.id}`}
              className={`h-5 w-5 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                selected ? '' : 'border-2 border-input'
              }`}
            >
              {selected && <CheckMark className="h-5 w-5" />}
            </div>
          )}
          {/* Attempt history sits at the head of the chip strip: the strip
              scrolls horizontally, and this is the one chip that must never be
              the part scrolled out of sight. */}
          <QuestionAttemptBadge summary={attemptSummary} showNew={attemptsTracked} />
          <span className="text-xs px-2 py-0.5 rounded-full border border-input text-muted-foreground bg-background shrink-0">
            {question.exam}
          </span>
          {question.wiki_link.map(link => (
            <span
              key={link}
              className="text-xs px-2 py-0.5 rounded-full border border-input text-muted-foreground bg-background shrink-0"
            >
              {conceptLabel(link)}
            </span>
          ))}
        </div>
        {/* The quiz's Info: where the question came from and its fact check.
            Its dialog is portalled, but React bubbles its clicks through this
            wrapper — stopped here, or every press inside it would also toggle
            the row's selection. The list already shows topic and difficulty,
            so the study metadata is no hint here. */}
        <div className="shrink-0" onClick={e => e.stopPropagation()}>
          <QuestionInfoButton
            question={question}
            open={showInfo}
            onOpenChange={setShowInfo}
            showStudyMeta
            className="min-h-0 px-2 py-1.5 sm:px-2.5"
          />
        </div>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
          className="text-sm px-3 py-1.5 rounded-md hover:bg-accent transition-colors shrink-0"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Collapsed: a few lines of the stem, clamped so a long one can't push
          the next row off screen. Expanded: the whole thing. */}
      <div className="text-sm text-muted-foreground leading-relaxed">
        {expanded ? (
          <MarkdownText className={STEM_MD_CLASS}>{question.stem}</MarkdownText>
        ) : (
          <span className="line-clamp-3">
            <StemPreview question={question} query={query} />
          </span>
        )}
      </div>

      {expanded && (
        <div className="pt-1 space-y-1" onClick={e => e.stopPropagation()}>
          {question.options.map(opt => {
            const isCorrect = showAnswer && opt.key === question.answer
            return (
              <div
                key={opt.key}
                className={`flex gap-2 text-sm rounded px-2 py-1 ${
                  isCorrect ? 'bg-green-100 dark:bg-green-950 text-green-900 dark:text-green-200' : ''
                }`}
              >
                <span className="font-medium text-muted-foreground shrink-0">{opt.key})</span>
                <span><MarkdownText inline>{opt.text}</MarkdownText></span>
              </div>
            )
          })}

          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowAnswer(v => !v)}
              className="text-sm px-4 py-2 rounded-md hover:bg-accent transition-colors"
            >
              {showAnswer ? 'Hide answer' : 'Show answer'}
            </button>
          </div>

          {showAnswer && <QuestionAnswerReveal question={question} />}
        </div>
      )}
    </div>
  )
}
